/**
 * createHttpErrors — 프로젝트별 ErrorCatalog를 주입해 에러 유틸을 생성하는 팩토리
 *
 * 프로젝트마다 ErrorCatalog가 다르기 때문에 팩토리 패턴으로 분리한다.
 * 반환된 함수들은 카탈로그 키에 대한 타입 자동완성을 지원한다.
 *
 * @example
 * // shared/errors.ts (프로젝트 내 1회 설정)
 * import { createHttpErrors } from "@linkup/http";
 * import { ErrorCatalog } from "./errorCatalog";
 *
 * export const { throwHttpError, createHttpError, errorHandler } =
 *   createHttpErrors(ErrorCatalog, {
 *     onMysqlDupEntry: (sqlMessage) => {
 *       if (sqlMessage?.includes("product_domae_tb_unique")) return "이미 등록된 도매사 상품입니다.";
 *     },
 *   });
 *
 * @example
 * // route 에서 사용
 * import { throwHttpError } from "@/shared/errors";
 * throwHttpError("NOT_FOUND", "회원을 찾을 수 없습니다.");
 */

import { NextResponse } from "next/server";
import { ZodError } from "zod";

/** ErrorCatalog 각 항목의 형태 */
export interface ErrorEntry {
    status: number;
    message: string;
}

/** AppError — throwHttpError / createHttpError 가 만드는 에러 객체 */
export interface AppError extends Error {
    status: number;
    code: string;
    detail?: string;
}

export interface HttpErrorsOptions {
    /**
     * MySQL ER_DUP_ENTRY 발생 시 호출된다.
     * 프로젝트 고유 유니크 키 이름으로 detail 메시지를 분기할 때 사용.
     * undefined를 반환하면 기본 메시지("이미 등록된 데이터입니다.")를 사용한다.
     */
    onMysqlDupEntry?: (sqlMessage?: string) => string | undefined;
    /**
     * 에러 로깅 핸들러.
     * 기본: development 환경에서만 console.error.
     */
    onLog?: (err: unknown) => void;
}

export function createHttpErrors<
    TCatalog extends Record<string, ErrorEntry>
>(catalog: TCatalog, options: HttpErrorsOptions = {}) {
    type ErrorKey = keyof TCatalog & string;

    const log =
        options.onLog ??
        ((err: unknown) => {
            if (process.env.NODE_ENV === "development") {
                console.error("[http] error:", err);
            }
        });

    /** 에러를 throw한다. never 타입이므로 이후 코드가 실행되지 않는다. */
    function throwHttpError(code: ErrorKey, detail?: string): never {
        const entry = catalog[code];
        const error = new Error(entry.message) as AppError;
        error.status = entry.status;
        error.code = code;
        if (detail) error.detail = detail;
        throw error;
    }

    /** 에러 객체를 생성만 하고 throw하지 않는다. */
    function createHttpError(code: ErrorKey, detail?: string): AppError {
        const entry = catalog[code];
        const error = new Error(entry.message) as AppError;
        error.status = entry.status;
        error.code = code;
        if (detail) error.detail = detail;
        return error;
    }

    /** catch 블록에서 받은 unknown 에러를 NextResponse로 변환한다. */
    function errorHandler(err: unknown): NextResponse {
        log(err);

        // AppError (throwHttpError / createHttpError 로 만든 에러)
        const isAppError = (e: unknown): e is AppError =>
            typeof e === "object" &&
            e !== null &&
            "code" in e &&
            "status" in e;

        // mysql2 에러 — errno(number)가 있으면 mysql 에러로 판별
        const isMysqlError = (
            e: unknown
        ): e is { code?: string; errno?: number; sqlMessage?: string; message?: string } =>
            typeof e === "object" &&
            e !== null &&
            "errno" in e &&
            typeof (e as any).errno === "number";

        let code: string = "INTERNAL_ERROR";
        let status = 500;
        let message = "Internal Server Error";
        let detail = "알 수 없는 오류";
        let fieldErrors: { path: string; message: string }[] | undefined;

        if (isAppError(err)) {
            code = err.code;
            status = err.status;
            message = err.message || catalog[code]?.message || message;
            detail = err.detail || err.message || detail;
        } else if (err instanceof ZodError) {
            fieldErrors = err.issues.map((issue) => ({
                path: issue.path.join("."),
                message: issue.message,
            }));
            code = "BAD_REQUEST";
            status = 400;
            // "입력값이 올바르지 않습니다" 뒤에 필드명(마지막 path 세그먼트) + zod 원본 메시지를 힌트로 붙여준다.
            message = `입력값이 올바르지 않습니다: ${fieldErrors
                .map((e) => {
                    const word = e.path.split(".").pop();
                    return word ? `${word}: ${e.message}` : e.message;
                })
                .join(", ")}`;
            detail = fieldErrors
                .map((e) => (e.path ? `${e.path}: ${e.message}` : e.message))
                .join(", ");
        } else if (isMysqlError(err)) {
            if (err.code === "ER_DUP_ENTRY") {
                code = "BAD_REQUEST";
                status = 400;
                message = "중복된 데이터입니다.";
                detail =
                    options.onMysqlDupEntry?.(err.sqlMessage) ??
                    "이미 등록된 데이터입니다.";
            } else {
                message = err.message || message;
                detail = err.sqlMessage || err.message || detail;
            }
        } else if (err instanceof Error) {
            message = err.message || message;
            detail = err.message || detail;
        }

        return NextResponse.json(
            {
                success: false,
                status,
                code,
                message,
                detail,
                fieldErrors,
                error: process.env.NODE_ENV === "development" ? message : undefined,
            },
            { status }
        );
    }

    return { throwHttpError, createHttpError, errorHandler };
}
